/**
 * Per-tab permission card for role-group modules.
 */
import * as React from 'react';
import type { InterfaceElement } from '@/types/interface-elements.types';
import type { RoleBindingRule, RuleReadScope } from '@/types/role.types';
import { ReadScopeBuilder } from './ReadScopeBuilder';
import { Checkbox } from '@/components/forms/Checkbox';
import { DynamicIcon } from '@/components/forms/IconPicker';
import {
  findPerRoleAction,
  tabElementRoleCode,
  toggleTabPermAction,
} from '@/components/roles/role-form-utils';
import { UserPlus, Pencil, Trash2 } from 'lucide-react';

/** Props for {@link ActionRow}. */
interface ActionRowProps {
  icon: React.ReactNode;
  iconBg: string;
  label: string;
  checked: boolean;
  onToggle: (v: boolean) => void;
  disabled?: boolean;
  checkedBorder?: string;
  children?: React.ReactNode;
}

/** One permission action row with optional nested scope. */
function ActionRow({
  icon,
  iconBg,
  label,
  checked,
  onToggle,
  disabled,
  checkedBorder = 'border-blue-200 bg-blue-50/40',
  children,
}: Readonly<ActionRowProps>) {
  return (
    <div
      className={`rounded-lg border transition-all ${checked ? checkedBorder : 'border-gray-200 bg-white'}`}
    >
      <div className="flex items-center gap-3 p-3">
        <div className={`flex h-9 w-9 shrink-0 items-center justify-center rounded-lg ${iconBg}`}>
          {icon}
        </div>

        <span className="flex-1 text-sm font-medium text-gray-800">{label}</span>

        <Checkbox checked={checked} onCheckedChange={(v) => onToggle(!!v)} disabled={disabled} />
      </div>

      {children && <div className="px-3 pb-3 pt-0">{children}</div>}
    </div>
  );
}

/** Props for {@link RoleTabPermCard}. */
interface RoleTabPermCardProps {
  /** The tab element representing this role type. */
  tab: InterfaceElement;
  /** Current rule for this role (null if none configured yet). */
  rule: RoleBindingRule | null;
  /**
   * Only per-role actions (option-create-*, option-edit-*, option-delete-*).
   * Global module actions (export, reactivate…) are handled at the {@link ModuleAccessCard} level.
   */
  perRoleActions: InterfaceElement[];
  onUpdate: (updated: Partial<RoleBindingRule>) => void;
  disabled?: boolean;
}

/** Per-tab permission card for role-group modules. */
export function RoleTabPermCard({
  tab,
  rule,
  perRoleActions,
  onUpdate,
  disabled,
}: Readonly<RoleTabPermCardProps>): React.ReactElement {
  const roleCode = tabElementRoleCode(tab.elementId);
  const roleConfig = tab.config || {};
  const createAction = findPerRoleAction(perRoleActions, 'create-role', roleCode);
  const editAction = findPerRoleAction(perRoleActions, 'edit-role', roleCode);
  const deleteAction = findPerRoleAction(perRoleActions, 'delete-role', roleCode);

  const selectedActions = rule?.actions ?? [];
  const writeScopes = rule?.writeScopes ?? {};

  const isChecked = (action: InterfaceElement | undefined) =>
    action ? selectedActions.includes(action.elementId) : false;

  const toggle = (action: InterfaceElement, checked: boolean) => {
    onUpdate(
      toggleTabPermAction(selectedActions, writeScopes, action.elementId, checked, rule?.read),
    );
  };

  const setWriteScope = (action: InterfaceElement, scope: RuleReadScope | null) => {
    onUpdate({ writeScopes: { ...writeScopes, [action.elementId]: scope } });
  };

  return (
    <div className="bg-white rounded-lg border border-gray-200 p-4 space-y-4">
      <div className="flex items-center gap-2 pb-3 border-b border-gray-100">
        {(roleConfig as any).icon && (
          <DynamicIcon name={(roleConfig as any).icon} className="w-5 h-5 text-gray-600" />
        )}
        <h5 className="text-sm font-semibold text-gray-800">{tab.elementLabel}</h5>
      </div>

      <div className="space-y-2">
        <div className="text-xs font-semibold text-gray-500 uppercase tracking-wider">
          Périmètre de lecture
        </div>
        <ReadScopeBuilder
          value={rule?.read ?? null}
          onChange={(read) => onUpdate({ read })}
          disabled={disabled}
          label="Quelles données peut-il voir ?"
          previewPrefix="Voit seulement"
        />
      </div>

      {(createAction || editAction || deleteAction) && (
        <div className="space-y-3">
          <div className="text-xs font-semibold text-gray-500 uppercase tracking-wider">
            Actions
          </div>

          {createAction && (
            <ActionRow
              icon={<UserPlus className="w-4 h-4" />}
              iconBg="bg-green-100 text-green-600"
              label="Peut créer"
              checked={isChecked(createAction)}
              onToggle={(v) => toggle(createAction, v)}
              disabled={disabled}
            />
          )}

          {editAction && (
            <ActionRow
              icon={<Pencil className="w-4 h-4" />}
              iconBg="bg-blue-100 text-blue-600"
              label="Peut modifier"
              checked={isChecked(editAction)}
              onToggle={(v) => toggle(editAction, v)}
              disabled={disabled}
              checkedBorder="border-blue-200 bg-blue-50/40"
            >
              {isChecked(editAction) && (
                <ReadScopeBuilder
                  value={writeScopes[editAction.elementId] ?? null}
                  onChange={(scope) => setWriteScope(editAction, scope)}
                  disabled={disabled}
                  label="Sur quelles données peut-il modifier ?"
                  constrainedBy={rule?.read ?? null}
                />
              )}
            </ActionRow>
          )}

          {deleteAction && (
            <ActionRow
              icon={<Trash2 className="w-4 h-4" />}
              iconBg="bg-red-100 text-red-600"
              label="Peut supprimer"
              checked={isChecked(deleteAction)}
              onToggle={(v) => toggle(deleteAction, v)}
              disabled={disabled}
              checkedBorder="border-red-100 bg-red-50/30"
            >
              {isChecked(deleteAction) && (
                <ReadScopeBuilder
                  value={writeScopes[deleteAction.elementId] ?? null}
                  onChange={(scope) => setWriteScope(deleteAction, scope)}
                  disabled={disabled}
                  label="Sur quelles données peut-il supprimer ?"
                  constrainedBy={rule?.read ?? null}
                />
              )}
            </ActionRow>
          )}
        </div>
      )}

      {!createAction && !editAction && !deleteAction && (
        <p className="text-sm text-gray-500 italic">Aucune action configurée pour ce type.</p>
      )}
    </div>
  );
}
